"use client";

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Only import the default fallback language (Hebrew) synchronously.
// Other languages are loaded on-demand when the user switches language.
import he from '../locales/he/common.json';

// NOTE: language resolution (saved choice > ?lang= > device language) lives in
// `src/components/providers/i18n-provider.tsx`, which switches the language only
// AFTER hydration has committed (double rAF) to avoid a hydration mismatch.
// i18n therefore always initializes in Hebrew here; the previous
// getInitialLanguage()/initLng pair was dead code and is removed.

// Lazy-load language bundles to reduce initial JS payload
// Hebrew is always available (fallback), others loaded on demand.
const languageLoaders: Record<string, () => Promise<Record<string, unknown>>> = {
  ru: () => import('../locales/ru/common.json').then(m => m.default),
  en: () => import('../locales/en/common.json').then(m => m.default),
  fr: () => import('../locales/fr/common.json').then(m => m.default),
};

async function loadLanguageBundle(lng: string): Promise<void> {
  if (lng === 'he') return; // Already loaded
  if (i18n.hasResourceBundle(lng, 'common')) return; // Already loaded previously
  
  const loader = languageLoaders[lng];
  if (loader) {
    try {
      const bundle = await loader();
      i18n.addResourceBundle(lng, 'common', bundle, true, true);
    } catch (err) {
      console.error(`Failed to load language bundle for ${lng}:`, err);
    }
  }
}

// Initialize i18n with Hebrew only (saves ~130KB from initial bundle)
i18n
  .use(initReactI18next)
  .init({
    resources: {
      he: { common: he },
    },
    ns: ['common'],
    defaultNS: 'common',
    lng: 'he', // Always start with Hebrew, then switch if needed
    fallbackLng: 'he',
    supportedLngs: ['he', 'ru', 'en', 'fr'],
    interpolation: {
      escapeValue: false, 
    },
    // Don't show warnings for missing keys while language is loading
    parseMissingKeyHandler: (key: string) => key,
  });

// NOTE: We deliberately do NOT switch the language at module-load time.
// Language selection (URL ?lang= > saved choice > device language) is handled
// exclusively by I18nProvider in a useEffect that runs AFTER React hydrates the
// Hebrew-prerendered static HTML. Switching here (before/at hydration) changed
// the rendered text mid-hydration and caused a hydration mismatch in the footer.

// On language change: load bundle first, then switch
const originalChangeLanguage = i18n.changeLanguage.bind(i18n);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(i18n as any).changeLanguage = async (lng?: string, ...args: any[]) => {
  if (lng && lng !== 'he') {
    await loadLanguageBundle(lng);
  }
  return originalChangeLanguage(lng, ...args);
};

// Save language changes to localStorage
i18n.on('languageChanged', (lng) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('i18nextLng', lng);
  }
});

export default i18n;
